import * as css from 'css/exports.module.scss';

import './InitiativesTableActionBar.scss';

import { ConfirmDeleteButton } from '@ui/modals';
import { InitiativesTableQueryParams } from './InitiativesTable';
import { InitiativeStatusSelector } from '../StatusSelector';
import { SearchInput } from '@ui/general';
import { UserSelector } from '@ui/UserSelector';
import { When } from 'react-if';

import { Checkbox, Space } from 'antd';
import { COPY_SPECIFIC, INITIATIVES_SEARCH_SETTINGS, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { coreActions, FeatureFlag, Initiative } from '@sonar-software/sonar-core';
import React, { useCallback } from 'react';
import {
	UpdateQueryParams,
	useAction,
	useCurrentUser,
	useFeatureFlag,
	UseSelectionTableWithInversionResult
} from '@hooks';

export interface InitiativesTableActionBarProps {
	selectionData: UseSelectionTableWithInversionResult<Initiative, number>;
	onUpdate: UpdateQueryParams<InitiativesTableQueryParams>;
	queryParams: InitiativesTableQueryParams;
}

export const InitiativesTableActionBar: React.FC<InitiativesTableActionBarProps> = ({
	selectionData,
	onUpdate,
	queryParams
}) => {
	const { selectAllEnabled, selectedKeys, selectedCount, resetSelection } = selectionData;
	const { id: userId } = useCurrentUser();
	const jiraIntegration = useFeatureFlag(FeatureFlag.JiraIntegration);

	const deleteInitiatives = useAction(coreActions.requestDeleteCoreInitiatives);
	const deleteBulkInitiatives = useAction(coreActions.requestDeleteCoreInitiativesBulk);
	const updateInitiatives = useAction(coreActions.requestPatchCoreInitiatives);
	const updateBulkInitiatives = useAction(coreActions.requestPatchCoreInitiativesBulk);

	const assignedTos = queryParams.assignedTos ?? [];
	const assignedToMe = assignedTos.length === 1 && assignedTos[ 0 ] === userId;

	const handleOnSearch = useCallback(
		(value: string) => {
			onUpdate({ title: value });
		},
		[ onUpdate ]
	);

	const handleAssignToMeChange = useCallback(() => {
		onUpdate({ assignedTos: assignedToMe ? [] : [ userId ] });
	}, [ assignedToMe, onUpdate, userId ]);

	const handleBulkChange = useCallback(
		(changes: Partial<Initiative>) => {
			const initiativeIds = selectedKeys.map((id) => Number(id));

			if (selectAllEnabled) {
				updateBulkInitiatives({
					...queryParams,
					initiativeIdsToExclude: initiativeIds,
					patchInitiativeDescription: {
						...changes
					}
				});
			} else {
				updateInitiatives({
					...queryParams,
					initiativeIds,
					patchInitiativeDescription: {
						...changes
					}
				});
			}

			resetSelection();
		},
		[ queryParams, resetSelection, selectAllEnabled, selectedKeys, updateBulkInitiatives, updateInitiatives ]
	);

	const handleBulkArchive = useCallback(() => {
		const initiativeIds = selectedKeys.map((id) => Number(id));

		if (selectAllEnabled) {
			deleteBulkInitiatives({
				...queryParams,
				initiativeIdsToExclude: initiativeIds
			});
		} else {
			deleteInitiatives({ initiativeIds });
		}

		resetSelection();
	}, [ deleteBulkInitiatives, deleteInitiatives, queryParams, resetSelection, selectAllEnabled, selectedKeys ]);

	return (
		<div className='initiatives-table-action-bar'>
			<Space size={ Number(css.size16) }>
				<SearchInput
					debounceDelay={ INITIATIVES_SEARCH_SETTINGS.SEARCH_DELAY }
					id='initiatives-table-search'
					onChange={ handleOnSearch }
					placeholder={ PLACEHOLDERS_COPY.SEARCH_BY_TITLE } />
				<Checkbox
					checked={ assignedToMe }
					id='initiative-table-self-assigned-filter'
					onChange={ handleAssignToMeChange }>
					{ COPY_SPECIFIC.ASSIGNED_TO_ME }
				</Checkbox>
			</Space>
			<When condition={ selectedCount }>
				<Space size={ Number(css.size24) }>
					<ConfirmDeleteButton
						buttonId='initiatives-table-archive-button'
						buttonLabel={ COPY_SPECIFIC.ARCHIVE }
						cancelButtonProps={ {
							id: 'initiatives-table-archive-cancel-button'
						} }
						confirmButtonProps={ {
							id: 'initiatives-table-archive-confirm-button',
							value: COPY_SPECIFIC.ARCHIVE
						} }
						onConfirm={ handleBulkArchive }
						title={ COPY_SPECIFIC.ARE_YOU_SURE_ARCHIVE_INITIATVES.replace('{0}', String(selectedCount)) }
						type='link' />
					<When condition={ !jiraIntegration }>
						<InitiativeStatusSelector
							defaultValue={ COPY_SPECIFIC.CHANGE_STATUS }
							id='initiatives-table-status-select'
							onChange={ (statusId) => handleBulkChange({ initiativeStatusId: statusId }) }
							showArrow={ false }
							variant='borderless' />
					</When>
					<UserSelector
						id='initiatives-table-user-select'
						onSingleChange={ (id) => handleBulkChange({ assignedTo: Number(id) }) }
						placeholder={ COPY_SPECIFIC.CHANGE_OWNER }
						selectionRequired />
				</Space>
			</When>
		</div>
	);
};
